import { useCallback, useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose,
  DatePicker,
  Input
} from '@goodneighbor/ui';
import { Settings04, XClose } from '@goodneighbor/ui/icons/untitled';
import {
  ModalProvider,
  OverlayProvider,
  UNSAFE_PortalProvider
} from 'react-aria';
import type { DateValue } from 'react-aria-components';
import styles from './eventFiltersDialog.module.scss';
import { toDateString } from '@/services/dateTime';
import { cx } from 'class-variance-authority';
import { activityTypeMap, causeTypeMap, skillTypeMap } from '@/services/event';

export type EventFiltersDialogProps = {
  startDateValue: DateValue | null;
  endDateValue: DateValue | null;
  handleDateChange: (field: 'start' | 'end', value: string | null) => void;
  clearFilters: () => void;
  selectedCauses: string[];
  availableCauses: string[];
  availableActivityTypeIds: string[];
  activityTypes: { id: string; name: string }[];
  activityTypeFilters: string[];
  availableSkillTypeIds: string[];
  skillTypes: { id: string; name: string }[];
  skillTypeFilters: string[];
  onToggleCause: (cause: string) => void;
  onToggleActivityType: (activityType: string) => void;
  onToggleSkillType: (skillType: string) => void;
  volunteersMinValue: string | null;
  volunteersMaxValue: string | null;
  onVolunteersNeededChange: (field: 'min' | 'max', value: string) => void;
  totalEvents: number;
  includeClosedEvents: boolean;
  onToggleIncludeClosedEvents: () => void;
  activeFilterCount: number;
};

export default function EventFiltersDialog({
  startDateValue,
  endDateValue,
  handleDateChange,
  clearFilters,
  selectedCauses,
  availableCauses,
  availableActivityTypeIds,
  activityTypes,
  activityTypeFilters,
  availableSkillTypeIds,
  skillTypes,
  skillTypeFilters,
  onToggleCause,
  onToggleActivityType,
  onToggleSkillType,
  volunteersMinValue,
  volunteersMaxValue,
  onVolunteersNeededChange,
  totalEvents,
  includeClosedEvents,
  onToggleIncludeClosedEvents,
  activeFilterCount
}: EventFiltersDialogProps) {
  const [filterDialogContainer, setFilterDialogContainer] =
    useState<HTMLDivElement | null>(null);

  const handleFilterDialogRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node || filterDialogContainer === node) {
        return;
      }
      setFilterDialogContainer(node);
    },
    [filterDialogContainer]
  );

  return (
    <Dialog>
      <div className={styles.filterButtonWrapper}>
        <DialogTrigger asChild>
          <Button
            color="secondary"
            iconLeading={<Settings04 />}
            aria-label="Filters"
            className={styles.filterButton}
          />
        </DialogTrigger>
        {activeFilterCount > 0 && (
          <span
            className={styles.filterBadge}
            aria-label={`${activeFilterCount} active filters`}
          >
            {activeFilterCount}
          </span>
        )}
      </div>
      <DialogContent
        ref={handleFilterDialogRef}
        className={styles.filterContent}
      >
        <OverlayProvider>
          <ModalProvider>
            {filterDialogContainer ? (
              <UNSAFE_PortalProvider getContainer={() => filterDialogContainer}>
                <FiltersContent
                  startDateValue={startDateValue}
                  endDateValue={endDateValue}
                  handleDateChange={handleDateChange}
                  filterDialogContainer={filterDialogContainer}
                  clearFilters={clearFilters}
                  selectedCauses={selectedCauses}
                  availableCauses={availableCauses}
                  availableActivityTypeIds={availableActivityTypeIds}
                  activityTypes={activityTypes}
                  activityTypeFilters={activityTypeFilters}
                  availableSkillTypeIds={availableSkillTypeIds}
                  skillTypes={skillTypes}
                  skillTypeFilters={skillTypeFilters}
                  onToggleCause={onToggleCause}
                  onToggleActivityType={onToggleActivityType}
                  onToggleSkillType={onToggleSkillType}
                  volunteersMinValue={volunteersMinValue}
                  volunteersMaxValue={volunteersMaxValue}
                  onVolunteersNeededChange={onVolunteersNeededChange}
                  totalEvents={totalEvents}
                  includeClosedEvents={includeClosedEvents}
                  onToggleIncludeClosedEvents={onToggleIncludeClosedEvents}
                />
              </UNSAFE_PortalProvider>
            ) : (
              <FiltersContent
                startDateValue={startDateValue}
                endDateValue={endDateValue}
                handleDateChange={handleDateChange}
                filterDialogContainer={filterDialogContainer}
                clearFilters={clearFilters}
                selectedCauses={selectedCauses}
                availableCauses={availableCauses}
                availableActivityTypeIds={availableActivityTypeIds}
                activityTypes={activityTypes}
                activityTypeFilters={activityTypeFilters}
                availableSkillTypeIds={availableSkillTypeIds}
                skillTypes={skillTypes}
                skillTypeFilters={skillTypeFilters}
                onToggleCause={onToggleCause}
                onToggleActivityType={onToggleActivityType}
                onToggleSkillType={onToggleSkillType}
                volunteersMinValue={volunteersMinValue}
                volunteersMaxValue={volunteersMaxValue}
                onVolunteersNeededChange={onVolunteersNeededChange}
                totalEvents={totalEvents}
                includeClosedEvents={includeClosedEvents}
                onToggleIncludeClosedEvents={onToggleIncludeClosedEvents}
              />
            )}
          </ModalProvider>
        </OverlayProvider>
      </DialogContent>
    </Dialog>
  );
}

export type FiltersContentProps = {
  startDateValue: DateValue | null;
  endDateValue: DateValue | null;
  handleDateChange: (field: 'start' | 'end', value: string | null) => void;
  filterDialogContainer: HTMLDivElement | null;
  clearFilters: () => void;
  selectedCauses: string[];
  availableCauses: string[];
  availableActivityTypeIds: string[];
  activityTypes: { id: string; name: string }[];
  activityTypeFilters: string[];
  availableSkillTypeIds: string[];
  skillTypes: { id: string; name: string }[];
  skillTypeFilters: string[];
  onToggleCause: (cause: string) => void;
  onToggleActivityType: (activityType: string) => void;
  onToggleSkillType: (skillType: string) => void;
  volunteersMinValue: string | null;
  volunteersMaxValue: string | null;
  onVolunteersNeededChange: (field: 'min' | 'max', value: string) => void;
  totalEvents: number;
  includeClosedEvents: boolean;
  onToggleIncludeClosedEvents: () => void;
};

function FiltersContent({
  startDateValue,
  endDateValue,
  handleDateChange,
  filterDialogContainer,
  clearFilters,
  selectedCauses,
  availableCauses,
  availableActivityTypeIds,
  activityTypes,
  activityTypeFilters,
  availableSkillTypeIds,
  skillTypes,
  skillTypeFilters,
  onToggleCause,
  onToggleActivityType,
  onToggleSkillType,
  volunteersMinValue,
  volunteersMaxValue,
  onVolunteersNeededChange,
  totalEvents,
  includeClosedEvents,
  onToggleIncludeClosedEvents
}: FiltersContentProps) {
  return (
    <>
      <DialogHeader>
        <DialogTitle>Filters</DialogTitle>
      </DialogHeader>
      <div className={styles.filterBody}>
        <div className={styles.filterSection}>
          <label className={styles.filterLabel}>Date Range</label>
          <div className={styles.dateInputs}>
            <div className={styles.inputGroup}>
              <label className={styles.inputLabel}>Start Date</label>
              <div className={styles.datePickerRow}>
                <DatePicker
                  placeholder="Start Date"
                  value={startDateValue}
                  onChange={(date) =>
                    handleDateChange('start', toDateString(date))
                  }
                  popoverContainer={filterDialogContainer}
                  showActions={false}
                  shouldCloseOnSelect
                  fullWidth
                  aria-label="Start date"
                />
                {startDateValue && (
                  <Button
                    type="button"
                    color="tertiary"
                    iconLeading={<XClose />}
                    aria-label="Clear start date"
                    className={styles.clearDateButton}
                    onClick={() => handleDateChange('start', null)}
                  />
                )}
              </div>
            </div>
            <div className={styles.inputGroup}>
              <label className={styles.inputLabel}>End Date</label>
              <div className={styles.datePickerRow}>
                <DatePicker
                  placeholder="End Date"
                  value={endDateValue}
                  onChange={(date) =>
                    handleDateChange('end', toDateString(date))
                  }
                  popoverContainer={filterDialogContainer}
                  showActions={false}
                  shouldCloseOnSelect
                  fullWidth
                  aria-label="End date"
                />
                {endDateValue && (
                  <Button
                    type="button"
                    color="tertiary"
                    iconLeading={<XClose />}
                    aria-label="Clear end date"
                    className={styles.clearDateButton}
                    onClick={() => handleDateChange('end', null)}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
        <div className={styles.filterSection}>
          <label className={styles.filterLabel}>Cause Type</label>
          <div className={styles.pillsFilter}>
            {availableCauses.length === 0 ? (
              <span className={styles.emptyPillFilterLabel}>
                No causes available
              </span>
            ) : (
              availableCauses.map((cause) => {
                const isSelected = selectedCauses.includes(cause);
                return (
                  <Button
                    type="button"
                    key={cause}
                    color="secondary"
                    onClick={() => onToggleCause(cause)}
                    className={cx(
                      styles.pillFilterItem,
                      styles.causePill,
                      isSelected && styles.causePillSelected
                    )}
                  >
                    {causeTypeMap[cause] || cause}
                  </Button>
                );
              })
            )}
          </div>
        </div>
        <div className={styles.filterSection}>
          <label className={styles.filterLabel}>Activity Type</label>
          <div className={styles.pillsFilter}>
            {availableActivityTypeIds.length === 0 ? (
              <span className={styles.emptyPillFilterLabel}>
                No activity types available
              </span>
            ) : (
              availableActivityTypeIds.map((activityTypeId) => {
                const activityTypeName = activityTypes.find(
                  (activityType) => activityType.id === activityTypeId
                )?.name;
                const activityTypeLabel =
                  activityTypeName && activityTypeMap[activityTypeName];
                const isSelected = activityTypeFilters.includes(activityTypeId);
                return (
                  <Button
                    type="button"
                    key={activityTypeId}
                    color="secondary"
                    onClick={() => onToggleActivityType(activityTypeId)}
                    className={cx(
                      styles.pillFilterItem,
                      styles.activityTypePill,
                      isSelected && styles.activityTypePillSelected
                    )}
                  >
                    {activityTypeLabel || activityTypeName}
                  </Button>
                );
              })
            )}
          </div>
        </div>
        <div className={styles.filterSection}>
          <label className={styles.filterLabel}>Skill Type</label>
          <div className={styles.pillsFilter}>
            {availableSkillTypeIds.length === 0 ? (
              <span className={styles.emptyPillFilterLabel}>
                No skill types available
              </span>
            ) : (
              availableSkillTypeIds.map((skillTypeId) => {
                const skillTypeName = skillTypes.find(
                  (skillType) => skillType.id === skillTypeId
                )?.name;
                const skillTypeLabel =
                  skillTypeName && skillTypeMap[skillTypeName];
                const isSelected = skillTypeFilters.includes(skillTypeId);
                return (
                  <Button
                    type="button"
                    key={skillTypeId}
                    color="secondary"
                    onClick={() => onToggleSkillType(skillTypeId)}
                    className={cx(
                      styles.pillFilterItem,
                      styles.skillTypePill,
                      isSelected && styles.skillTypePillSelected
                    )}
                  >
                    {skillTypeLabel || skillTypeName}
                  </Button>
                );
              })
            )}
          </div>
        </div>
        <div className={styles.filterSection}>
          <label className={styles.filterLabel}>Volunteers Needed</label>
          <div className={styles.volunteerInputs}>
            <div className={styles.inputGroup}>
              <label className={styles.inputLabel}>Min</label>
              <Input
                placeholder="0"
                inputMode="numeric"
                value={volunteersMinValue || ''}
                onChange={(value) => onVolunteersNeededChange('min', value)}
                aria-label="Minimum volunteers needed"
              />
            </div>
            <div className={styles.inputGroup}>
              <label className={styles.inputLabel}>Max</label>
              <Input
                placeholder="0"
                inputMode="numeric"
                value={volunteersMaxValue || ''}
                onChange={(value) => onVolunteersNeededChange('max', value)}
                aria-label="Maximum volunteers needed"
              />
            </div>
          </div>
        </div>
        <div className={styles.filterSection}>
          <Checkbox
            isSelected={includeClosedEvents}
            onChange={onToggleIncludeClosedEvents}
            label="Include registration closed events"
            size="md"
          />
        </div>
      </div>
      <DialogFooter className={styles.filterFooter}>
        <Button color="tertiary" onClick={clearFilters}>
          Reset
        </Button>
        <DialogClose asChild>
          <Button className={styles.showButton} color="primary">
            Show {totalEvents} events
          </Button>
        </DialogClose>
      </DialogFooter>
    </>
  );
}
